/* eslint-disable @typescript-eslint/no-explicit-any */
import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { all, call, delay, fork, put, race, select, take, takeLatest, takeLeading } from 'redux-saga/effects';
import { PropertyBinding } from 'three';

import { httpDELETE, httpGET, httpGETBlob, httpPOST, httpPUT } from '../../services/http.ts';
import { BatchPresignedUrlResponse, FileType, PresignedUrlResponse } from '../../types/file.ts';
import { Lamp } from '../../types/lamp.ts';
import { LampWireframeThirdPartyEntry, LampWireframesState } from '../../types/lampWireframes.ts';
import { Light } from '../../types/light.ts';
import { Mesh, MeshGroup } from '../../types/mesh.ts';
import { ModelFile, ModelLayer } from '../../types/model.ts';
import { LampSimilarityDetail, Project, RankedCandidate, Version } from '../../types/project.ts';
import { revokeThirdPartyWireframeUrls } from '../../util/lampWireframes.ts';
import { createObjBlobUrl, fetchLdcAndBuildWireframeWithGrid } from '../../util/ldc';
import {
  canPersistMarketMatchGroupChanges,
  isAutopositioningInProgress as checkIsAutopositioningInProgress,
  isBakingJobCompleted as checkIsBakingJobCompleted,
  isCalculationCompleted as checkIsCalculationCompleted,
  isCalculationInProgress as checkIsCalculationInProgress,
  isCalculationStarted as checkIsCalculationStarted,
} from '../../utils/jobStatus.ts';
import { clampMaxApexAngle } from '../../utils/maxApexAngle.ts';
import { shouldUseFrontendMeasurementSource } from '../../utils/measurementCalculation/measurementSource.ts';
import { requestFrontendMeasurementCalculation } from '../../utils/measurementCalculation/trigger.ts';
import { requirementForSave, requirementFromMesh } from '../../utils/photometricTargets.ts';
import { lampsHaveChanged, meshGroupsHaveChanged, modelLayersHaveChanged } from '../../utils/workspaceDirty.ts';
import { toastReducer } from '../reducers/toast.reducer.ts';
import {
  addLampsInternal,
  addModel,
  applyLampSimilarityMeasurement,
  cancelAutoPositioning,
  clearLampWireframes,
  clearThirdPartyLampSimilarity,
  completeFileDownload,
  createEmptyVersion,
  createVersionFromMarketMatch,
  deleteMarketMatchGroup,
  deleteVersion,
  doClearLampWireframes,
  doClearThirdPartyLampSimilarity,
  doClearWorkspaceState,
  downloadBlenderProject,
  downloadIesFile,
  downloadReportImages,
  duplicateVersion,
  finishAutoPositioning,
  finishCancelAutoPositioning,
  finishCreateVersion,
  finishLampSimilarity,
  finishTamanOptimization,
  loadBakedModelFile,
  loadLampSimilarityDetails,
  loadMeasurementFile,
  loadProject,
  loadWireframeFiles,
  patchThirdPartyWireframeRotation,
  removeLampInternal,
  renameVersion,
  resetFileProgress,
  resetLampHistory,
  setActiveViewMode,
  setAutosaveStatus,
  setFrontendMeasurementData,
  setLampSimilarityDetails,
  setLoadingLampSimilarityDetails,
  setMeasurementSource,
  setMeshGroups,
  setModelLayers,
  setProject,
  setTamanWireframes,
  setThirdPartyWireframes,
  startAutoPositioning,
  startFileDownload,
  startLampSimilarity,
  startPeriodicChecking,
  startTamanOptimization,
  stopPeriodicChecking,
  switchActiveVersion,
  triggerClearWorkspaceState,
  updateActiveVersion,
  updateFileProgress,
  updateLampMarketMatchGroup,
  updateLampProductVariant,
  updateLampRotationRadians,
  updateProjectVersion,
  updateProjectVersionFailed,
  updateProjectVersionSucceeded,
} from '../reducers/workspace.reducer.ts';
import type { AutosaveStatus, MeasurementData } from '../reducers/workspace.reducer.ts';
import { getUserFeatureFlags } from '../selectors/featureFlags.selector.ts';
import { RootState, history } from '../store.ts';

const { showToast } = toastReducer.actions;

const getActiveVersion = (state: RootState): Version | undefined => {
  const project = state.workspace.project;
  if (!project || !project.activeVersionId || !project.versions) return undefined;
  return project.versions.find((v) => v.id === project.activeVersionId);
};

interface ProjectWithModelFiles extends Project {
  modelFilesIds?: number[];
}

function revokeBlobUrl(url?: string | null): void {
  if (!url || !url.startsWith('blob:')) return;
  try {
    URL.revokeObjectURL(url);
  } catch {
    /* ignore revoke errors */
  }
}

function revokeProjectBakedModelUrls(project: Project | null | undefined): void {
  if (!project?.versions?.length) return;
  for (const version of project.versions) {
    revokeBlobUrl(version.bakedModelUrl);
  }
}

function revokeUploadedModelUrls(uploadedModels: ModelFile[] | null | undefined): void {
  if (!uploadedModels?.length) return;
  for (const model of uploadedModels) {
    revokeBlobUrl(model.url);
  }
}

// Helper function to get batch presigned URLs
function* getBatchPresignedUrls(ids: number[]): Generator<unknown, BatchPresignedUrlResponse[], any> {
  const { data }: AxiosResponse<BatchPresignedUrlResponse[]> = yield call(
    httpPOST,
    '/files/presigned-url/batch-download',
    { ids },
  );
  return data;
}

function* fetchWithProgress(url: string, fileId: string): Generator<unknown, Response, any> {
  const response = yield call(fetch, url);

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const contentLength = response.headers.get('content-length');
  const total = contentLength ? parseInt(contentLength, 10) : 0;

  if (!total) {
    return response;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    return response;
  }

  const chunks: Uint8Array[] = [];
  let receivedLength = 0;

  while (true) {
    const { done, value } = yield call([reader, reader.read]);

    if (done) break;

    chunks.push(value);
    receivedLength += value.length;

    yield put(updateFileProgress({ fileId, downloadedSize: receivedLength }));
  }

  // Create new response with the collected data
  const blob = new Blob(chunks as BlobPart[]);
  return new Response(blob, {
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
  });
}

export function* handleTamanOptimization(): Generator<unknown, void, any> {
  try {
    // Get the current project ID and active version ID from the state
    const project = yield select((state: RootState) => state.workspace.project);

    if (!project || !project.activeVersionId) {
      console.error('No project or active version found');
      yield put(finishTamanOptimization());
      return;
    }

    const projectId = project.id;
    const versionId = project.activeVersionId;

    const modelLayers = (yield select((state: RootState) => state.workspace.modelLayers)) as ModelLayer[];
    const hasIlluminatedLayer = modelLayers.some((layer: ModelLayer) => layer.isIlluminated);

    if (!hasIlluminatedLayer) {
      yield put(
        toastReducer.actions.showToast({
          message: 'workspace.toast.atLeastOneLayerIlluminated',
          type: 'warning',
        }),
      );

      // Don't start optimization
      yield put(finishTamanOptimization());
      return;
    }

    // Check if calculation is already in progress
    const activeVersion: Version | undefined = yield select(getActiveVersion);
    const isCalculationInProgress = checkIsCalculationInProgress(activeVersion);

    // Only update status and start calculation if not already in progress
    if (!isCalculationInProgress && project.versions) {
      yield call(checkForChanges);

      const maxApexAngleDegrees = clampMaxApexAngle(
        (yield select((state: RootState) => state.workspace.maxApexAngleDegrees)) as number,
      );

      const { data: updatedVersion }: AxiosResponse<Version> = yield call(
        httpPOST,
        `/projects/${projectId}/versions/${versionId}/calculation/start`,
        { maxApexAngleDegrees },
      );

      // Update only the active version in state
      yield put(updateActiveVersion(updatedVersion));
    } else {
      yield put(finishTamanOptimization());
    }
  } catch (error: any) {
    console.error('Error starting TAMAN optimization:', error);

    // Check if it's a 401 error - the axios interceptor will handle the redirect
    // Don't show additional error messages for auth errors as the user will be redirected
    if (error?.response?.status !== 401) {
      yield put(finishTamanOptimization());
      yield put(
        showToast({
          message: error?.response?.data?.message || 'workspace.toast.optimizationStartFailed',
          type: 'error',
        }),
      );
    }
  }
}

export function* handleAutoPositioning(): Generator<unknown, void, any> {
  // Get the current project ID and layers from the state
  const project: Project = yield select((state: RootState) => state.workspace.project);
  const projectId = project?.id;
  const modelLayers = yield select((state: RootState) => state.workspace.modelLayers);
  const versionId = project?.activeVersionId as number;
  const activeVersion: Version | undefined = yield select(getActiveVersion);

  if (!project || !project.activeVersionId) {
    console.error('No project or active version found');
    yield put(finishAutoPositioning());
    return;
  }

  // Check if autopositioning is already in progress
  const isAutoPositioningInProgress = checkIsAutopositioningInProgress(activeVersion);

  // If autopositioning is already in progress, just return without starting a new process
  if (isAutoPositioningInProgress) {
    yield put(finishAutoPositioning());
    return;
  }

  // Check if there's at least one layer that can hold light
  const hasLayerThatCanHoldLight = modelLayers.some((layer: ModelLayer) => layer.canHoldLuminaire);

  if (!hasLayerThatCanHoldLight) {
    // Show a toaster warning
    yield put(
      toastReducer.actions.showToast({
        message: 'workspace.toast.atLeastOneLayerLuminaire',
        type: 'warning',
      }),
    );

    // Don't start autopositioning
    yield put(finishAutoPositioning());
    return;
  }

  try {
    // Check for changes before starting autopositioning
    yield call(checkForChanges);

    const desiredLuminaireHeight = (yield select(
      (state: RootState) => state.workspace.desiredLuminaireHeight,
    )) as number;
    const norms = ((yield select((state: RootState) => state.user?.norms)) ?? []) as { id: number; name: string }[];

    // Create MeshCreateRequest objects from the current layers
    const meshes = modelLayers.map((layer: ModelLayer) => {
      const requirement = requirementForSave(layer, norms);
      return {
        uuid: layer.uuid,
        groupId: layer.groupId,
        name: layer.originalName,
        displayName: layer.displayName,
        color: layer.color,
        type: layer.layerType.toUpperCase(),
        normName: requirement.normName,
        ...(requirement.targets ? { targets: requirement.targets } : {}),
        canHoldLight: layer.canHoldLuminaire,
        desiredLuminaireHeight: layer.canHoldLuminaire
          ? (layer.desiredLuminaireHeight ?? desiredLuminaireHeight)
          : null,
      };
    });

    const { data: updatedVersion }: AxiosResponse<Version> = yield call(
      httpPOST,
      `/projects/${projectId}/versions/${versionId}/lights/autoposition`,
      {
        meshes: meshes,
        maxApexAngleDegrees: 77.5,
      },
    );

    // Update only the active version in state
    yield put(updateActiveVersion(updatedVersion));
  } catch (error: any) {
    console.error('Error starting autopositioning:', error);
    if (error?.response?.status !== 401) {
      yield put(
        toastReducer.actions.showToast({
          message: error?.response?.data?.message || 'workspace.toast.autopositioningStartFailed',
          type: 'error',
        }),
      );
    }
  } finally {
    yield put(finishAutoPositioning());
  }
}

function* handleCancelAutoPositioning(): Generator<unknown, void, any> {
  const project: Project = yield select((state: RootState) => state.workspace.project);
  const activeVersion: Version | undefined = yield select(getActiveVersion);
  const projectId = project?.id;
  const versionId = project?.activeVersionId as number | undefined;
  const jobId = activeVersion?.autopositioningJob?.id;

  if (!projectId || !versionId || !jobId) {
    return;
  }

  // Only cancel if autopositioning is currently in progress or scheduled
  if (!checkIsAutopositioningInProgress(activeVersion)) {
    return;
  }

  try {
    const { data: updatedVersion }: AxiosResponse<Version> = yield call(
      httpPOST,
      `/projects/${projectId}/versions/${versionId}/job/${jobId}/cancel`,
      {},
    );
    if (updatedVersion) {
      yield put(updateActiveVersion(updatedVersion));
    }
  } catch (error: any) {
    console.error('Error canceling autopositioning:', error);
    if (error?.response?.status !== 401) {
      yield put(
        toastReducer.actions.showToast({
          message: 'workspace.toast.cancelAutopositioningFailed',
          type: 'error',
        }),
      );
    }
  } finally {
    yield put(finishCancelAutoPositioning());
  }
}

export function* handleUpdateProjectVersion(
  action: ReturnType<typeof updateProjectVersion>,
): Generator<unknown, void, any> {
  // Loop so edits made while a PUT is in flight are flushed before we
  // report success. takeLeading ignores overlapping dispatches; this loop
  // is what picks up the newer local state afterward.
  let { projectId, versionId, optimisticLockKey } = action.payload;

  while (true) {
    try {
      const lamps = (yield select((state: RootState) => state.workspace.lamps)) as any[];
      const modelLayers = (yield select((state: RootState) => state.workspace.modelLayers)) as ModelLayer[];
      const norms = (yield select((state: RootState) => state.user.norms)) as any[];
      const meshGroups = (yield select((state: RootState) => state.workspace.meshGroups)) as MeshGroup[];

      // Snapshot the scene we're about to persist. Baseline (prev*) advances
      // only after the server accepts this payload — never before.
      const lampsSnapshot = JSON.parse(JSON.stringify(lamps));
      const modelLayersSnapshot = JSON.parse(JSON.stringify(modelLayers));
      const meshGroupsSnapshot = JSON.parse(JSON.stringify(meshGroups));

      const lights: Light[] = lamps.map((lamp) => ({
        name: lamp.name,
        uuid: lamp.uuid,
        positionX: lamp.position[0].toString(),
        positionY: lamp.position[1].toString(),
        positionZ: lamp.position[2].toString(),
        distanceToGround: lamp.distanceToGround,
        // Preserve catalogue product/variant assignments (market-matched versions).
        productId: lamp.productId,
        variantId: lamp.variantId,
        seriesId: lamp.seriesId,
        manufacturerId: lamp.manufacturerId,
        rotationRadians: lamp.rotationRadians,
        marketMatchGroup: lamp.marketMatchGroup,
      }));

      // Convert model layers to meshes format
      const meshes: Mesh[] = modelLayers.map((layer) => {
        const requirement = requirementForSave(layer, norms);
        return {
          name: layer.originalName,
          displayName: layer.displayName,
          color: layer.color,
          type: layer.layerType.toUpperCase(),
          uuid: layer.uuid,
          groupId: layer.groupId,
          normId: requirement.normId ?? undefined,
          normName: requirement.normName,
          ...(requirement.targets ? { targets: requirement.targets } : {}),
          canHoldLight: layer.canHoldLuminaire,
          desiredLuminaireHeight: layer.desiredLuminaireHeight,
          isIlluminated: layer.isIlluminated,
        };
      });

      const requestBody = {
        optimisticLockKey,
        sceneData: {
          lights,
          meshes,
          meshGroups,
        },
      };

      const { data } = (yield call(
        httpPUT,
        `/projects/${projectId}/versions/${versionId}`,
        requestBody,
      )) as AxiosResponse<Version>;

      // Persist succeeded for this snapshot — advance the dirty baseline.
      prevLamps = lampsSnapshot;
      prevModelLayers = modelLayersSnapshot;
      prevMeshGroups = meshGroupsSnapshot;

      const activeVersion: Version | undefined = yield select(getActiveVersion);
      if (activeVersion && activeVersion.id === versionId) {
        yield put(updateActiveVersion(data));
      }

      // User may have edited during the request; keep saving until caught up.
      const stillDirty: boolean = yield call(hasUnsavedWorkspaceEdits);
      if (stillDirty) {
        const latestVersion: Version | undefined = yield select(getActiveVersion);
        const project: Project = yield select((state: RootState) => state.workspace.project);
        if (!project || !latestVersion || latestVersion.id !== versionId) {
          yield put(updateProjectVersionSucceeded({ versionId }));
          return;
        }
        projectId = project.id;
        versionId = latestVersion.id;
        optimisticLockKey = latestVersion.optimisticLockKey;
        // Throttle catch-up saves so a burst of edits (e.g. dragging) doesn't
        // turn into a back-to-back stream of PUTs. Keeps the indicator in
        // "saving" until we're caught up, without hammering the server.
        yield delay(AUTOSAVE_CATCHUP_DELAY_MS);
        continue;
      }

      yield put(updateProjectVersionSucceeded({ versionId }));
      return;
    } catch (error: any) {
      console.error('Error updating project version:', error);

      // Check if it's a 409 conflict error
      if (error.response?.status === 409) {
        // Show toast suggesting to reload the page
        yield put(
          showToast({
            message: 'workspace.toast.versionConflict',
            type: 'warning',
          }),
        );
      } else {
        // Show generic error toast for other errors
        yield put(
          showToast({
            message: 'workspace.toast.updateVersionFailed',
            type: 'error',
          }),
        );
      }
      // Do not advance prev* — next autosave tick can retry the unsaved edits.
      yield put(updateProjectVersionFailed({ versionId }));
      return;
    }
  }
}

// Delay between catch-up PUTs while flushing edits made during an in-flight save.
const AUTOSAVE_CATCHUP_DELAY_MS = 1000;
// How often the navbar indicator re-checks for unsaved local edits.
const DIRTY_STATUS_POLL_MS = 500;

// Store for previous state to detect changes
let prevLamps: any[] = [];
let prevModelLayers: any[] = [];
let prevMeshGroups: any[] = [];
/** False until startPeriodicChecking snapshots the baseline — avoids empty-prev false dirty. */
let dirtyBaselineReady = false;
let periodicCheckTask: any = null;
let dirtyCheckTask: any = null;

function didMarketMatchGroupsChange(lamps: any[], previousLamps: any[]): boolean {
  if (lamps.length !== previousLamps.length) return false;

  const prevGroupByLampId = new Map(previousLamps.map((lamp) => [lamp.id, lamp.marketMatchGroup]));
  if (!lamps.every((lamp) => prevGroupByLampId.has(lamp.id))) return false;

  return lamps.some((lamp) => lamp.marketMatchGroup !== prevGroupByLampId.get(lamp.id));
}

/** Same dirty notion as autosave — also used by WebSocket reconnect recovery. */
export function* hasUnsavedWorkspaceEdits(): Generator<unknown, boolean, any> {
  if (!dirtyBaselineReady) return false;

  const project: Project = yield select((state: RootState) => state.workspace.project);
  const lamps = yield select((state: RootState) => state.workspace.lamps);
  const modelLayers = yield select((state: RootState) => state.workspace.modelLayers);
  const meshGroups = yield select((state: RootState) => state.workspace.meshGroups);

  if (!modelLayers || modelLayers.length === 0) return false;
  if (!project || !project.activeVersionId) return false;

  const activeVersion: Version | undefined = yield select(getActiveVersion);
  if (!activeVersion) return false;

  const calculationWasStarted = checkIsCalculationStarted(activeVersion);
  const autopositioningWasStarted = checkIsAutopositioningInProgress(activeVersion);
  const marketMatchGroupsChanged = didMarketMatchGroupsChange(lamps, prevLamps);

  if (marketMatchGroupsChanged && canPersistMarketMatchGroupChanges(activeVersion)) {
    return true;
  }

  if (calculationWasStarted || autopositioningWasStarted) {
    return false;
  }

  return (
    lampsHaveChanged(lamps, prevLamps) ||
    modelLayersHaveChanged(modelLayers, prevModelLayers) ||
    meshGroupsHaveChanged(meshGroups, prevMeshGroups)
  );
}

function* checkForChanges(): Generator<unknown, boolean, any> {
  try {
    if (!dirtyBaselineReady) {
      return false;
    }

    const project: Project = yield select((state: RootState) => state.workspace.project);
    const lamps = yield select((state: RootState) => state.workspace.lamps);
    const modelLayers = yield select((state: RootState) => state.workspace.modelLayers);
    const meshGroups = yield select((state: RootState) => state.workspace.meshGroups);

    // Skip periodic updates if layers are empty
    if (!modelLayers || modelLayers.length === 0) {
      return false;
    }

    if (!project || !project.activeVersionId) {
      return false;
    }

    const activeVersion: Version | undefined = yield select(getActiveVersion);
    if (!activeVersion) {
      return false;
    }

    const calculationWasStarted = checkIsCalculationStarted(activeVersion);
    const autopositioningWasStarted = checkIsAutopositioningInProgress(activeVersion);

    const marketMatchGroupsChanged = didMarketMatchGroupsChange(lamps, prevLamps);

    // After optimization completes, only market-match group edits may be persisted.
    if (marketMatchGroupsChanged && canPersistMarketMatchGroupChanges(activeVersion)) {
      // Do not advance prev* here — handleUpdateProjectVersion does that on success.
      yield put(
        updateProjectVersion({
          projectId: project.id,
          versionId: activeVersion.id,
          optimisticLockKey: activeVersion.optimisticLockKey,
        }),
      );
      return true;
    }

    // Skip periodic updates if calculation was already started or if auto positioning is in progress
    if (calculationWasStarted || autopositioningWasStarted) {
      return false;
    }

    // Check if lamps have changed (name, position or catalogue product/variant assignment)
    const lampsChanged = lampsHaveChanged(lamps, prevLamps);

    // Check if layers have changed (ignores ephemeral UI like visibility)
    const layersChanged = modelLayersHaveChanged(modelLayers, prevModelLayers);

    // Check if mesh groups have changed
    const meshGroupsChanged = meshGroupsHaveChanged(meshGroups, prevMeshGroups);

    // If any has changed, update the project version
    if (lampsChanged || layersChanged || meshGroupsChanged) {
      console.log('Changes detected, updating project version');

      // Do not advance prev* until the PUT succeeds — otherwise mid-flight
      // edits look "saved" and failed saves would never retry.
      yield put(
        updateProjectVersion({
          projectId: project.id,
          versionId: activeVersion.id,
          optimisticLockKey: activeVersion.optimisticLockKey,
        }),
      );
      return true;
    } else {
      // No changes detected
      return false;
    }
  } catch (error) {
    console.error('Error in checkForChanges saga:', error);
    return false;
  }
}

function* periodicCheckForChanges(): Generator<unknown, void, any> {
  while (true) {
    yield call(checkForChanges);
    yield delay(5000);
  }
}

/** Flips the navbar indicator to "Saving…" as soon as local edits exist,
 *  without waiting for the 5s autosave tick to fire the PUT.
 *
 *  Only promotes from `saved` — never from `error`. Otherwise, since a failed
 *  save leaves the edits dirty, this poll would immediately flip `error` back
 *  to `saving` (well before the next retry tick actually runs), making the
 *  indicator flap between the two. The error state is owned by the save path. */
function* periodicDirtyStatusCheck(): Generator<unknown, void, any> {
  while (true) {
    const dirty: boolean = yield call(hasUnsavedWorkspaceEdits);
    if (dirty) {
      const currentStatus: AutosaveStatus = yield select((state: RootState) => state.workspace.autosaveStatus);
      if (currentStatus === 'saved') {
        yield put(setAutosaveStatus('saving'));
      }
    }
    yield delay(DIRTY_STATUS_POLL_MS);
  }
}

function* handleStartPeriodicChecking(): Generator<unknown, void, any> {
  // Cancel existing periodic check if any
  if (periodicCheckTask) {
    periodicCheckTask.cancel();
  }
  if (dirtyCheckTask) {
    dirtyCheckTask.cancel();
  }

  // Initialize previous snapshot with current state to avoid immediate update on first tick
  const lamps = (yield select((state: RootState) => state.workspace.lamps)) as any[];
  const modelLayers = (yield select((state: RootState) => state.workspace.modelLayers)) as any[];
  const meshGroups = (yield select((state: RootState) => state.workspace.meshGroups)) as any[];
  prevLamps = JSON.parse(JSON.stringify(lamps));
  prevModelLayers = JSON.parse(JSON.stringify(modelLayers));
  prevMeshGroups = JSON.parse(JSON.stringify(meshGroups));
  dirtyBaselineReady = true;

  // Start new periodic check and store the task
  periodicCheckTask = yield fork(periodicCheckForChanges);
  dirtyCheckTask = yield fork(periodicDirtyStatusCheck);
}

function handleStopPeriodicChecking() {
  if (periodicCheckTask) {
    periodicCheckTask.cancel();
    periodicCheckTask = null;
  }
  if (dirtyCheckTask) {
    dirtyCheckTask.cancel();
    dirtyCheckTask = null;
  }
  dirtyBaselineReady = false;
}

function revokeLampWireframeUrls(lampWireframes: RootState['workspace']['lampWireframes']): void {
  for (const f of lampWireframes.tamanWireframes ?? []) {
    try {
      URL.revokeObjectURL(f.url);
    } catch {
      /* ignore revoke errors */
    }
  }
  revokeThirdPartyWireframeUrls(lampWireframes.thirdPartyWireframes ?? []);
}

function* handleClearLampWireframes(): Generator<unknown, void, any> {
  const lampWireframes: RootState['workspace']['lampWireframes'] = yield select(
    (state: RootState) => state.workspace.lampWireframes,
  );
  revokeLampWireframeUrls(lampWireframes);
  yield put(doClearLampWireframes());
}

function* handleClearThirdPartyLampSimilarity(): Generator<unknown, void, any> {
  const thirdParty: RootState['workspace']['lampWireframes']['thirdPartyWireframes'] = yield select(
    (state: RootState) => state.workspace.lampWireframes.thirdPartyWireframes,
  );
  revokeThirdPartyWireframeUrls(thirdParty);
  yield put(doClearThirdPartyLampSimilarity());
}

function* handleClearWorkspaceState(): Generator<unknown, void, any> {
  const workspaceState = (yield select((state: RootState) => state.workspace)) as RootState['workspace'];
  revokeLampWireframeUrls(workspaceState.lampWireframes);
  revokeProjectBakedModelUrls(workspaceState.project);
  revokeUploadedModelUrls(workspaceState.uploadedModels);
  yield call(handleStopPeriodicChecking);
  prevLamps = [];
  prevModelLayers = [];
  prevMeshGroups = [];
  dirtyBaselineReady = false;
  yield put(doClearWorkspaceState());
}

function* handleSwitchActiveVersion(action: ReturnType<typeof switchActiveVersion>): Generator<unknown, void, any> {
  try {
    const { projectId, versionId } = action.payload;
    const versionBeforeSwitch: Version | undefined = yield select(getActiveVersion);
    const currentProjectBeforeSwitch: Project | null = yield select((state: RootState) => state.workspace.project);

    // Check for changes in the current version before switching
    const changesMade: boolean = (yield call(checkForChanges)) as boolean;
    if (changesMade && versionBeforeSwitch) {
      yield race({
        saved: take(
          (a: any) => a.type === updateProjectVersionSucceeded.type && a.payload.versionId === versionBeforeSwitch.id,
        ),
        failed: take(
          (a: any) => a.type === updateProjectVersionFailed.type && a.payload.versionId === versionBeforeSwitch.id,
        ),
        timeout: delay(10_000),
      });
    }

    // Stop periodic checking for the old version
    yield call(handleStopPeriodicChecking);

    // Ensure we switch away from the version's default measurement data,
    // not an applied market-match override.
    const appliedSimilaritySelection = (yield select(
      (state: RootState) => state.workspace.appliedLampSimilaritySelection,
    )) as RootState['workspace']['appliedLampSimilaritySelection'];
    if (appliedSimilaritySelection !== null) {
      yield put(applyLampSimilarityMeasurement(appliedSimilaritySelection));
    }

    // Reset undo/redo history when switching versions
    yield put(resetLampHistory());

    // Always re-fetch latest project snapshot from backend on version switch.
    // This guarantees we don't carry stale in-memory version state across switches.
    const { data: freshProject }: AxiosResponse<ProjectWithModelFiles> = yield call(httpGET, `/projects/${projectId}`);
    const updatedProject: ProjectWithModelFiles = {
      ...freshProject,
      activeVersionId: versionId,
    };
    const newActiveVersion = updatedProject.versions.find((v) => v.id === versionId);
    if (!newActiveVersion) {
      throw new Error(`Target version not found: ${versionId}`);
    }

    // Release blob URLs from the previous in-memory project snapshot.
    revokeProjectBakedModelUrls(currentProjectBeforeSwitch);

    // Update project in state from fresh backend data
    yield put(setProject(updatedProject));

    // Clear stale wireframes/similarity only after version switch succeeds.
    // This avoids destructive cleanup when switching fails before setProject.
    yield put(clearLampWireframes());
    yield put(clearThirdPartyLampSimilarity());
    // Similarity details are version-scoped; clear so UI re-fetches for the selected version.
    yield put(setLampSimilarityDetails([]));

    if (newActiveVersion) {
      // Before changing content, validate if current view modes are allowed for this version
      const activeViewModes = yield select((state: RootState) => state.workspace.activeViewModes);

      // If user is on measurement view but calculation is not completed, switch to the object view
      if (activeViewModes?.measurement && !checkIsCalculationCompleted(newActiveVersion)) {
        yield put(setActiveViewMode('object'));
      }
      // If user is on render view but baked render is not completed, switch to the object view
      if (activeViewModes?.render && !checkIsBakingJobCompleted(newActiveVersion)) {
        yield put(setActiveViewMode('object'));
      }

      // Get norms from state
      const norms = (yield select((state: RootState) => state.user.norms)) as any[];

      // Clear existing lamps (use internal version to avoid history tracking)
      const existingLamps = (yield select((state: RootState) => state.workspace.lamps)) as any[];
      for (const lamp of existingLamps) {
        yield put(removeLampInternal(lamp.id));
      }

      // Add lamps from the new version (use internal version to avoid history tracking)
      if (newActiveVersion.lights && newActiveVersion.lights.length > 0) {
        const lamps = newActiveVersion.lights.map((light) => ({
          uuid: light.uuid,
          name: light.name,
          position: [light.positionX || 0, light.positionY || 0, light.positionZ || 0] as [number, number, number],
          distanceToGround: light.distanceToGround,
          lumenOutput: light.lumenOutput,
          iesFileId: light.iesFileId,
          productId: light.productId,
          variantId: light.variantId,
          seriesId: light.seriesId,
          manufacturerId: light.manufacturerId,
          rotationRadians: light.rotationRadians,
          marketMatchGroup: light.marketMatchGroup,
        }));
        yield put(addLampsInternal(lamps));
      }

      // Load layers from the new version's meshes
      if (newActiveVersion.meshes && newActiveVersion.meshes.length > 0) {
        const modelLayers = newActiveVersion.meshes.map((mesh) => {
          let layerType = 'Unspecified';
          if (mesh.type) {
            layerType = mesh.type.charAt(0).toUpperCase() + mesh.type.slice(1).toLowerCase();
          }
          const requirement = requirementFromMesh(mesh, norms);
          return {
            uuid: mesh.uuid,
            groupId: mesh.groupId,
            originalName: mesh.name,
            threeJsName: PropertyBinding.sanitizeNodeName(mesh.name),
            displayName: mesh.displayName,
            color: mesh.color,
            modelName: mesh.name,
            layerType: layerType,
            isIlluminated: requirement.isIlluminated,
            canHoldLuminaire: mesh.canHoldLight,
            selectedNorm: requirement.selectedNorm,
            customTargets: requirement.customTargets,
            visible: true,
          };
        });
        yield put(setModelLayers(modelLayers as any));
      }

      // Load mesh groups from the new version
      yield put(setMeshGroups(newActiveVersion.meshGroups || []));

      // Frontend measurement results are version-scoped; reset and select the
      // appropriate source. Use frontend engine when ALL lamps are catalogue-assigned;
      // fall back to ML otherwise (optimized versions, future hybrid).
      yield put(setFrontendMeasurementData(null));
      const userFeatureFlags = (yield select(getUserFeatureFlags)) as string[];
      yield put(
        setMeasurementSource(
          shouldUseFrontendMeasurementSource(userFeatureFlags, newActiveVersion) ? 'frontend' : 'ml',
        ),
      );

      // Load measurement file if available
      if (newActiveVersion.measurementFileId) {
        try {
          yield call(loadMeasurementFileData, newActiveVersion.measurementFileId, newActiveVersion.id);
        } catch (error) {
          console.error('Error loading measurement file in handleSwitchActiveVersion:', error);
        }
      }

      // Load the baked model file for the new version
      yield put(loadBakedModelFile());

      // Load wireframes for the new version (if showIESModels is on)
      yield put(loadWireframeFiles());

      // Refresh report images for the new version
      yield put(downloadReportImages());

      // Reset previous state for change detection
      prevLamps = [];
      prevModelLayers = [];
      prevMeshGroups = [];

      // Start periodic checking for the new version
      yield call(handleStartPeriodicChecking);
    }
  } catch (error) {
    console.error('Error switching active version:', error);
    yield put(
      toastReducer.actions.showToast({
        message: 'workspace.toast.switchVersionFailed',
        type: 'error',
      }),
    );
  }
}

function* handleLoadProject(action: ReturnType<typeof loadProject>): Generator<unknown, void, any> {
  const projectId = action.payload;
  const uploadedModels = (yield select((state: RootState) => state.workspace.uploadedModels)) as ModelFile[];
  const currentProject = (yield select((state: RootState) => state.workspace.project)) as Project | null;
  const { data } = (yield call(httpGET, `/projects/${projectId}`)) as AxiosResponse<ProjectWithModelFiles>;

  // Reset progress tracking
  yield put(resetFileProgress());

  // Release stale baked model blob URLs before replacing project snapshot.
  revokeProjectBakedModelUrls(currentProject);

  yield put(setProject(data));

  // Download report images for active version
  yield put(downloadReportImages());

  // Add lights from active version if available (use internal versions to avoid history tracking)
  if (data.activeVersionId && data.versions && data.versions.length > 0) {
    const activeVersion = data.versions.find((version) => version.id === data.activeVersionId);
    if (activeVersion && activeVersion.lights && activeVersion.lights.length > 0) {
      const existingLamps = (yield select((state: RootState) => state.workspace.lamps)) as any[];
      // Remove all existing lamps
      for (const lamp of existingLamps) {
        yield put(removeLampInternal(lamp.id));
      }

      const lamps = activeVersion.lights.map((light) => ({
        uuid: light.uuid,
        name: light.name,
        position: [light.positionX || 0, light.positionY || 0, light.positionZ || 0] as [number, number, number],
        distanceToGround: light.distanceToGround,
        lumenOutput: light.lumenOutput,
        iesFileId: light.iesFileId,
        productId: light.productId,
        variantId: light.variantId,
        seriesId: light.seriesId,
        manufacturerId: light.manufacturerId,
        rotationRadians: light.rotationRadians,
        marketMatchGroup: light.marketMatchGroup,
      }));
      yield put(addLampsInternal(lamps));
    }

    // Frontend measurement results are version-scoped; reset and select the
    // appropriate source. Use frontend engine when ALL lamps are catalogue-assigned;
    // fall back to ML otherwise (optimized versions, future hybrid).
    yield put(setFrontendMeasurementData(null));
    const userFeatureFlags = (yield select(getUserFeatureFlags)) as string[];
    yield put(
      setMeasurementSource(shouldUseFrontendMeasurementSource(userFeatureFlags, activeVersion) ? 'frontend' : 'ml'),
    );

    // Check if active version has a measurementFileId and load the measurement file
    if (activeVersion && activeVersion.measurementFileId) {
      // Check if measurement data is already loaded for this active version
      const versionInState: Version | undefined = yield select(getActiveVersion);
      const measurementData = versionInState?.measurementData;

      if (!measurementData || measurementData.length === 0) {
        try {
          yield call(loadMeasurementFileData, activeVersion.measurementFileId, activeVersion.id);
        } catch (error) {
          console.error('Error loading measurement file in handleLoadProject:', error);
        }
      }
    }

    // Load the baked model file
    yield put(loadBakedModelFile());
  }

  if (data.modelFilesIds && Array.isArray(data.modelFilesIds)) {
    const modelIdsToDownload = data.modelFilesIds.filter((fileId) => {
      return !uploadedModels.some((model) => model.name.includes(`Model ${fileId}`));
    });

    if (modelIdsToDownload.length === 0) {
      console.log('All models already loaded, skipping downloads');
      return;
    }

    console.log(`Downloading ${modelIdsToDownload.length} new models`);

    // Get batch presigned URLs for all model files
    const batchPresignedData: BatchPresignedUrlResponse[] = yield call(getBatchPresignedUrls, modelIdsToDownload);

    for (const presignedData of batchPresignedData) {
      const fileId = presignedData.id;

      // Initialize progress tracking
      yield put(
        startFileDownload({
          fileId: fileId.toString(),
          fileName: `Model ${fileId}`,
          fileType: FileType.OBJECT_3D,
          totalSize: presignedData.size,
        }),
      );

      const response = yield call(fetchWithProgress, presignedData.url, fileId.toString());
      const file = yield call([response, response.blob]);

      const url = URL.createObjectURL(file);

      let fileName = `Model ${fileId}`;

      const contentType = response.headers?.get('content-type');
      let fileExtension = '';

      if (contentType) {
        if (contentType.includes('model/gltf-binary')) {
          fileExtension = '.glb';
        } else if (contentType.includes('model/gltf+json')) {
          fileExtension = '.gltf';
        } else if (contentType.includes('model/obj')) {
          fileExtension = '.obj';
        }
      }

      if (!fileExtension) {
        const urlPath = presignedData.url.split('?')[0];
        const urlParts = urlPath.split('.');
        if (urlParts.length > 1) {
          const lastPart = urlParts.pop();
          fileExtension = lastPart ? `.${lastPart.toLowerCase()}` : '';
        }
      }

      if (!fileExtension) {
        fileExtension = '.glb';
      }

      fileName = `${fileName}${fileExtension}`;

      console.log(`Adding model with name: ${fileName}`);

      yield put(
        addModel({
          name: fileName,
          url: url,
        }),
      );

      // Mark download as complete
      yield put(completeFileDownload({ fileId: fileId.toString() }));
    }
  }
}

// Helper function to load measurement file data
export function* loadMeasurementFileData(fileId: number, targetVersionId?: number): Generator<unknown, void, any> {
  const projectInState = (yield select((state: RootState) => state.workspace.project)) as Project | null;
  const resolvedTargetVersionId = targetVersionId ?? projectInState?.activeVersionId;
  const targetVersion = projectInState?.versions?.find((v) => v.id === resolvedTargetVersionId);
  if (!resolvedTargetVersionId || !targetVersion) {
    return;
  }

  // If already present in target version, return cached value
  const cached = targetVersion.measurementData;
  if (cached && Array.isArray(cached) && cached.length > 0) {
    return;
  }

  // Get presigned URL for the measurement file
  const { data: presignedData }: AxiosResponse<PresignedUrlResponse> = yield call(
    httpGET,
    `/files/${fileId}/presigned-url/download`,
  );

  // Initialize progress tracking
  yield put(
    startFileDownload({
      fileId: fileId.toString(),
      fileName: 'Measurement File',
      fileType: FileType.POINTS_MAP,
      totalSize: presignedData.size,
    }),
  );

  try {
    // Download the file
    const response = yield call(fetchWithProgress, presignedData.url, fileId.toString());
    const fileContent = yield call([response, response.json]);

    // Process the measurement data
    const processedData = fileContent.map((item: any) => {
      // Create a percentage object with the same structure as expected/actual
      const percentage: any = {};

      // Calculate percentages for non-null values
      for (const key in item.actual) {
        if (item.actual[key] !== null && item.expected[key] !== null) {
          percentage[key] = item.actual[key] / item.expected[key];
        } else {
          percentage[key] = null;
        }
      }

      return {
        ...item,
        percentage,
      };
    });

    // Store the processed data on the target version in the state
    const latestProject = (yield select((state: RootState) => state.workspace.project)) as Project | null;
    const latestTargetVersion = latestProject?.versions?.find((v) => v.id === resolvedTargetVersionId);
    if (latestTargetVersion) {
      const updatedVersion: Version = { ...latestTargetVersion, measurementData: processedData };
      yield put(updateActiveVersion(updatedVersion));
    }
  } catch (err) {
    console.error('Error loading measurement file:', err);
    yield put(
      toastReducer.actions.showToast({
        message: 'workspace.toast.loadMeasurementDataFailed',
        type: 'error',
      }),
    );
  } finally {
    // Always mark download as complete to hide the progress overlay
    yield put(completeFileDownload({ fileId: fileId.toString() }));
  }
}

function* handleLoadMeasurementFile(action: ReturnType<typeof loadMeasurementFile>): Generator<unknown, void, any> {
  try {
    const fileId = action.payload;
    yield call(loadMeasurementFileData, fileId);
  } catch (error) {
    console.error('Error loading measurement file:', error);
    yield put(
      toastReducer.actions.showToast({
        message: 'workspace.toast.loadMeasurementFileFailed',
        type: 'error',
      }),
    );
  }
}

/** Minimal description of a luminaire with a catalogue product/variant assigned. */
type CatalogueLampDescriptor = {
  lampUuid: string;
  position: [number, number, number];
  productId?: number;
  variantId?: number;
  rotationRadians?: number;
};

/**
 * Builds IES wireframe entries for luminaires with catalogue product/variant
 * assignments by downloading each variant's LDC file. Products are fetched once
 * per distinct productId and wireframes built once per distinct variantId.
 */
function* buildCatalogueWireframeEntries(
  lamps: CatalogueLampDescriptor[],
): Generator<unknown, LampWireframeThirdPartyEntry[], any> {
  const entries: LampWireframeThirdPartyEntry[] = [];
  const productCache = new Map<number, { variants?: Array<{ id: string | number; ldcFileUrl?: string }> } | null>();
  const wireframeByVariantId = new Map<
    number,
    { url: string; grid: { azimuth: number[]; elevation: number[]; candela: number[][] } } | null
  >();

  for (const lamp of lamps) {
    if (lamp.productId == null || lamp.variantId == null) continue;

    let product = productCache.get(lamp.productId);
    if (product === undefined) {
      try {
        const { data }: AxiosResponse<{ variants?: Array<{ id: string | number; ldcFileUrl?: string }> }> = yield call(
          httpGET,
          `/catalogue/products/${lamp.productId}`,
        );
        product = data ?? null;
      } catch (e) {
        console.error(`Failed to fetch catalogue product ${lamp.productId} for IES wireframes:`, e);
        product = null;
      }
      productCache.set(lamp.productId, product);
    }

    const variant = product?.variants?.find((v) => v.id === lamp.variantId || String(v.id) === String(lamp.variantId));
    if (!variant?.ldcFileUrl) continue;

    let built = wireframeByVariantId.get(lamp.variantId);
    if (built === undefined) {
      try {
        const { obj, grid } = yield call(fetchLdcAndBuildWireframeWithGrid, variant.ldcFileUrl);
        built = { url: createObjBlobUrl(obj), grid };
      } catch (err) {
        console.error(`Failed to build IES wireframe for catalogue variant ${lamp.variantId}:`, err);
        built = null;
      }
      wireframeByVariantId.set(lamp.variantId, built);
    }
    if (!built) continue;

    entries.push({
      url: built.url,
      lampUuid: lamp.lampUuid,
      position: lamp.position,
      rotationRadians: lamp.rotationRadians,
      iesGrid: built.grid,
    });
  }

  return entries;
}

/**
 * Cheap rotation-only update: patches a single entry's rotationRadians in the
 * existing thirdPartyWireframes array without re-downloading any LDC geometry.
 */
function* handleUpdateWireframeRotation(
  action: ReturnType<typeof updateLampRotationRadians>,
): Generator<unknown, void, any> {
  const { id, rotationRadians } = action.payload;
  const lamps: Array<{ id: string; uuid: string }> = yield select((state: RootState) => state.workspace.lamps);
  const lamp = lamps.find((l) => l.id === id);
  if (!lamp) return;
  yield put(patchThirdPartyWireframeRotation({ lampUuid: lamp.uuid, rotationRadians }));
}

/**
 * (Re)builds third-party wireframes for all workspace lamps with a catalogue
 * variant assigned. Used by market-matched versions on load and after the user
 * swaps a luminaire's product/variant.
 */
function* handleRefreshCatalogueWireframes(): Generator<unknown, void, any> {
  try {
    const lamps = (yield select((state: RootState) => state.workspace.lamps)) as Array<{
      uuid: string;
      position: [number, number, number];
      productId?: number;
      variantId?: number;
      rotationRadians?: number;
    }>;
    const catalogueLamps = lamps.filter((l) => l.variantId != null);

    const existing: LampWireframeThirdPartyEntry[] | null = yield select(
      (state: RootState) => state.workspace.lampWireframes.thirdPartyWireframes,
    );
    revokeThirdPartyWireframeUrls(existing);

    if (catalogueLamps.length === 0) {
      yield put(setThirdPartyWireframes(null));
      return;
    }

    yield put(setThirdPartyWireframes(catalogueLamps.map((l) => ({ lampUuid: l.uuid }))));
    const entries: LampWireframeThirdPartyEntry[] = yield call(
      buildCatalogueWireframeEntries,
      catalogueLamps.map((l) => ({
        lampUuid: l.uuid,
        position: l.position,
        productId: l.productId,
        variantId: l.variantId,
        rotationRadians: l.rotationRadians,
      })),
    );
    yield put(setThirdPartyWireframes(entries.length > 0 ? entries : null));
  } catch (error) {
    console.error('Error refreshing catalogue wireframes:', error);
  }
}

export function* handleLoadWireframeFiles(): Generator<unknown, void, any> {
  try {
    const activeVersion: Version | undefined = yield select(getActiveVersion);
    const showIESModels = yield select((state: RootState) => state.workspace.showIESModels);
    const realTimeRender = yield select((state: RootState) => state.workspace.activeViewModes.realTimeRender);
    const lampWireframes: LampWireframesState = yield select((state: RootState) => state.workspace.lampWireframes);

    if (!showIESModels && !realTimeRender) return;

    // Skip if already cached for this version
    if (lampWireframes.versionId === activeVersion?.id && lampWireframes.tamanWireframes.length > 0) {
      return;
    }

    if (!activeVersion?.lights?.length) {
      console.log('No lights found in active version');
      return;
    }

    // Lights with a catalogue variant assigned (market-matched versions) get
    // wireframes from the variant's LDC file via the third-party channel.
    const catalogueLights = activeVersion.lights.filter((light) => light.variantId != null);
    if (catalogueLights.length > 0) {
      const existingThirdParty = lampWireframes.thirdPartyWireframes;
      const allLoaded =
        !!existingThirdParty &&
        catalogueLights.every((light) => existingThirdParty.some((e) => e.lampUuid === light.uuid && e.url));
      if (!allLoaded) {
        yield put(setThirdPartyWireframes(catalogueLights.map((light) => ({ lampUuid: light.uuid }))));
        const catalogueEntries: LampWireframeThirdPartyEntry[] = yield call(
          buildCatalogueWireframeEntries,
          catalogueLights.map((light) => ({
            lampUuid: light.uuid,
            position: [light.positionX, light.positionY, light.positionZ] as [number, number, number],
            productId: light.productId,
            variantId: light.variantId,
            rotationRadians: light.rotationRadians,
          })),
        );
        yield put(setThirdPartyWireframes(catalogueEntries.length > 0 ? catalogueEntries : null));
      }
    }

    // Build wireframes from raw IES files assigned to lamps
    const lightsWithIes = activeVersion.lights.filter((light) => !!light.iesFileId);

    if (lightsWithIes.length === 0) {
      console.log('No lamps with IES files found');
      return;
    }

    const downloadedFiles: {
      url: string;
      lampUuid: string;
      position: [number, number, number];
      iesFileId?: number;
      iesGrid?: { azimuth: number[]; elevation: number[]; candela: number[][] };
    }[] = [];
    const uniqueFileIds = Array.from(new Set(lightsWithIes.map((light) => light.iesFileId!)));
    const generatedWireframeByIesFileId = new Map<
      number,
      { url: string; grid: { azimuth: number[]; elevation: number[]; candela: number[][] } }
    >();

    // Get batch presigned URLs for raw IES files used by lights
    const fileIds = uniqueFileIds;
    const batchPresignedData: BatchPresignedUrlResponse[] = yield call(getBatchPresignedUrls, fileIds);
    const presignedById = new Map<number, string>(batchPresignedData.map((item) => [item.id, item.url]));

    for (const light of lightsWithIes) {
      try {
        const iesFileId = light.iesFileId!;
        let cached = generatedWireframeByIesFileId.get(iesFileId);

        if (!cached) {
          const presignedUrl = presignedById.get(iesFileId);
          if (!presignedUrl) {
            console.warn(`Missing presigned URL for IES file id ${iesFileId}`);
            continue;
          }

          const { obj, grid } = yield call(fetchLdcAndBuildWireframeWithGrid, presignedUrl);
          const wireframeUrl = createObjBlobUrl(obj);
          cached = { url: wireframeUrl, grid };
          generatedWireframeByIesFileId.set(iesFileId, cached);
        }

        downloadedFiles.push({
          url: cached.url,
          lampUuid: light.uuid,
          position: [light.positionX, light.positionY, light.positionZ],
          iesFileId,
          iesGrid: cached.grid,
        });
      } catch (error) {
        console.error(`Failed to build wireframe for lamp ${light.uuid}:`, error);
      }
    }

    if (activeVersion) {
      yield put(
        setTamanWireframes({
          versionId: activeVersion.id,
          entries: downloadedFiles,
        }),
      );
    }
  } catch (error) {
    console.error('Error loading wireframe files:', error);
    yield put(
      toastReducer.actions.showToast({
        message: 'workspace.toast.loadWireframeFilesFailed',
        type: 'error',
      }),
    );
  }
}

function* handleLoadBakedModelFile(): Generator<unknown, void, any> {
  try {
    // Get the active version from the state
    const activeVersion: Version | undefined = yield select(getActiveVersion);

    // If URL already present in state, skip downloading
    if (activeVersion?.bakedModelUrl) {
      return;
    }

    if (!activeVersion?.files?.length) {
      console.log('No files found in active version');
      return;
    }

    // Filter for BAKED_3D files
    const baked3dFiles = activeVersion.files.filter((file) => file.fileType === 'BAKED_3D');

    if (baked3dFiles.length === 0) {
      console.log('No BAKED_3D files found');
      return;
    }

    // Use the first BAKED_3D file found
    const file = baked3dFiles[0];

    try {
      // Get presigned URL for download
      const { data: presignedData }: AxiosResponse<PresignedUrlResponse> = yield call(
        httpGET,
        `/files/${file.id}/presigned-url/download`,
      );

      // Initialize progress tracking
      yield put(
        startFileDownload({
          fileId: file.id.toString(),
          fileName: 'Baked Model',
          fileType: FileType.BAKED_3D,
          totalSize: presignedData.size,
        }),
      );

      // Download the file and create a local URL
      const response = yield call(fetchWithProgress, presignedData.url, file.id.toString());
      const blob = yield call([response, response.blob]);
      const url = URL.createObjectURL(blob);

      // Store the downloaded file URL into the active version inside project
      if (activeVersion) {
        yield put(updateActiveVersion({ ...activeVersion, bakedModelUrl: url }));
      }

      // Mark download as complete
      yield put(completeFileDownload({ fileId: file.id.toString() }));
    } catch (error) {
      console.error(`Failed to download BAKED_3D file ${file.name}:`, error);
      // Clear bakedModelUrl on failure
      if (activeVersion) {
        yield put(updateActiveVersion({ ...activeVersion, bakedModelUrl: null }));
      }
    }
  } catch (error) {
    console.error('Error loading baked model file:', error);
    yield put(
      toastReducer.actions.showToast({
        message: 'workspace.toast.loadBakedModelFileFailed',
        type: 'error',
      }),
    );
  }
}

function* handleDownloadReportImages(): Generator<unknown, void, any> {
  try {
    // Get the active version from the state
    const activeVersion: Version | undefined = yield select(getActiveVersion);

    // If URLs already present in state, skip fetching
    const existing = activeVersion?.reportImageUrls;
    if (existing && Object.keys(existing).length > 0) {
      return;
    }

    if (!activeVersion?.files?.length) {
      console.log('No files found in active version');
      return;
    }

    // Filter for REPORT_IMAGE files (includes isocontour and false_color images with various photometry types)
    const reportImageFiles = activeVersion.files.filter((file) => file.fileType === 'REPORT_IMAGE');

    if (reportImageFiles.length === 0) {
      console.log('No REPORT_IMAGE files found');
      return;
    }

    const downloadedUrls: Record<string, string> = {};

    // Get batch presigned URLs for all REPORT_IMAGE files
    const fileIds = reportImageFiles.map((file) => file.id);
    const batchPresignedData: BatchPresignedUrlResponse[] = yield call(getBatchPresignedUrls, fileIds);

    for (const presignedData of batchPresignedData) {
      try {
        // Store the presigned URL directly (no need to download and create blob URL for images)
        downloadedUrls[presignedData.id.toString()] = presignedData.url;
      } catch (error) {
        console.error(`Failed to process presigned URL for REPORT_IMAGE file with id ${presignedData.id}:`, error);
      }
    }

    // Store the downloaded URLs on the active version in the state
    if (activeVersion) {
      const updatedVersion: Version = { ...activeVersion, reportImageUrls: downloadedUrls };
      yield put(updateActiveVersion(updatedVersion));
    }
  } catch (error) {
    console.error('Error loading report images:', error);
    yield put(
      toastReducer.actions.showToast({
        message: 'workspace.toast.loadReportImagesFailed',
        type: 'error',
      }),
    );
  }
}

function* handleDownloadIesFile(action: PayloadAction<number>): Generator<unknown, void, any> {
  try {
    const fileId = action.payload;

    // Get presigned URL for download
    const { data: presignedData }: AxiosResponse<PresignedUrlResponse> = yield call(
      httpGET,
      `/files/${fileId}/presigned-url/download`,
    );

    // Create a temporary anchor element to trigger download
    const link = document.createElement('a');
    link.href = presignedData.url;
    link.download = `lamp-${fileId}.ies`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Error downloading IES file:', error);
    yield put(
      toastReducer.actions.showToast({
        message: 'workspace.toast.downloadIesFileFailed',
        type: 'error',
      }),
    );
  }
}

function* handleDownloadBlenderProject(
  action: ReturnType<typeof downloadBlenderProject>,
): Generator<unknown, void, any> {
  const { projectId, versionId } = action.payload;
  try {
    const blob: Blob = yield call(httpGETBlob, `/projects/${projectId}/versions/${versionId}/blender-download`);
    const filename = `blender_project_${projectId}_version_${versionId}.zip`;

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (error) {
    console.error('Error downloading Blender project:', error);
    yield put(
      toastReducer.actions.showToast({
        message: 'workspace.toast.downloadBlenderProjectFailed',
        type: 'error',
      }),
    );
  }
}

export function* handleCreateEmptyVersion(
  action: ReturnType<typeof createEmptyVersion>,
): Generator<unknown, void, any> {
  try {
    const { projectId, sourceVersionId } = action.payload;

    const project: Project = yield select((state: RootState) => state.workspace.project);
    const sourceVersion = project.versions.find((v) => v.id === sourceVersionId);

    if (!sourceVersion) {
      throw new Error('Source version not found');
    }

    const payload = {
      name: 'Empty version',
      lights: [],
      meshes: sourceVersion.meshes.map((mesh) => ({
        uuid: mesh.uuid,
        groupId: mesh.groupId,
        name: mesh.name,
        displayName: mesh.displayName,
        color: mesh.color,
        normName: null,
        canHoldLight: false,
      })),
      meshGroups: sourceVersion.meshGroups || [],
    };

    const { data: newVersion }: AxiosResponse<Version> = yield call(
      httpPOST,
      `/projects/${projectId}/versions`,
      payload,
    );

    const updatedProject = {
      ...project,
      versions: [...project.versions, newVersion],
    };
    yield put(setProject(updatedProject));

    history.push(`/workspace?projectId=${projectId}&versionId=${newVersion.id}`);

    yield put(
      showToast({
        message: 'workspace.toast.emptyVersionCreated',
        type: 'success',
      }),
    );
  } catch (error) {
    console.error('Error creating empty version:', error);
    yield put(
      showToast({
        message: 'workspace.toast.createEmptyVersionFailed',
        type: 'error',
      }),
    );
  } finally {
    yield put(finishCreateVersion());
  }
}

function* handleDuplicateVersion(action: ReturnType<typeof duplicateVersion>): Generator<unknown, void, any> {
  try {
    const { projectId, sourceVersionId } = action.payload;

    const project: Project = yield select((state: RootState) => state.workspace.project);
    const sourceVersion = project.versions.find((v) => v.id === sourceVersionId);

    if (!sourceVersion) {
      throw new Error('Source version not found');
    }

    // Get model layers from the state to copy mesh configuration
    const modelLayers = (yield select((state: RootState) => state.workspace.modelLayers)) as ModelLayer[];
    const norms = ((yield select((state: RootState) => state.user?.norms)) ?? []) as { id: number; name: string }[];
    const payload = {
      name: `${sourceVersion.name} (copy)`,
      lights: sourceVersion.lights.map((light) => ({
        name: light.name,
        uuid: light.uuid,
        positionX: light.positionX,
        positionY: light.positionY,
        positionZ: light.positionZ,
        rotationX: light.rotationX || 0,
        rotationY: light.rotationY || 0,
        rotationZ: light.rotationZ || 0,
        latitude: light.latitude,
        longitude: light.longitude,
        distanceToGround: light.distanceToGround,
        marketMatchGroup: light.marketMatchGroup,
      })),
      meshes: modelLayers.map((layer) => {
        const requirement = requirementForSave(layer, norms);
        return {
          uuid: layer.uuid,
          groupId: layer.groupId,
          name: layer.originalName,
          displayName: layer.displayName,
          color: layer.color,
          type: layer.layerType.toUpperCase(),
          normName: requirement.normName || null,
          ...(requirement.targets ? { targets: requirement.targets } : {}),
          canHoldLight: layer.canHoldLuminaire,
          isIlluminated: layer.isIlluminated,
        };
      }),
      meshGroups: (yield select((state: RootState) => state.workspace.meshGroups)) as MeshGroup[],
    };

    const { data: newVersion }: AxiosResponse<Version> = yield call(
      httpPOST,
      `/projects/${projectId}/versions`,
      payload,
    );

    const updatedProject = {
      ...project,
      versions: [...project.versions, newVersion],
    };
    yield put(setProject(updatedProject));

    history.push(`/workspace?projectId=${projectId}&versionId=${newVersion.id}`);

    yield put(
      showToast({
        message: 'workspace.toast.versionDuplicated',
        type: 'success',
      }),
    );
  } catch (error) {
    console.error('Error duplicating version:', error);
    yield put(
      showToast({
        message: 'workspace.toast.duplicateVersionFailed',
        type: 'error',
      }),
    );
  } finally {
    yield put(finishCreateVersion());
  }
}

/**
 * Creates a new editable version from a market match candidate: copies the source
 * version's lights/layers and persists the candidate's catalogue product/variant
 * assignment on each placed luminaire. No measurement file is carried over —
 * measurements for market-matched versions are computed on the frontend.
 */
export function* handleCreateVersionFromMarketMatch(
  action: ReturnType<typeof createVersionFromMarketMatch>,
): Generator<unknown, void, any> {
  try {
    const { projectId, sourceVersionId, lampId, measurementFileId } = action.payload;

    const project: Project = yield select((state: RootState) => state.workspace.project);
    const sourceVersion = project.versions.find((v) => v.id === sourceVersionId);

    if (!sourceVersion) {
      throw new Error('Source version not found');
    }

    const candidate: RankedCandidate | undefined = sourceVersion.lampSimilarityResult?.candidates?.find(
      (c) => c.lampId === lampId && c.measurementFile?.id === measurementFileId,
    );
    if (!candidate) {
      throw new Error(`Market match candidate not found: ${lampId}`);
    }

    // Resolve the product name for the version name; fall back to the catalogue API
    // when similarity details are not loaded.
    const lampSimilarityDetails: LampSimilarityDetail[] = yield select(
      (state: RootState) => state.workspace.lampSimilarityDetails,
    );
    let productName = lampSimilarityDetails.find(
      (d) => d.lampId === lampId && d.measurementFileId === measurementFileId,
    )?.name;
    if (!productName) {
      try {
        const { data }: AxiosResponse<{ name?: string }> = yield call(httpGET, `/catalogue/products/${lampId}`);
        productName = data?.name;
      } catch (e) {
        console.error('Failed to fetch product name for market match version:', e);
      }
    }
    productName = productName || `Product #${lampId}`;

    const catalogueDetail = lampSimilarityDetails.find(
      (d) => d.lampId === lampId && d.measurementFileId === measurementFileId,
    );
    const catalogueSeriesId = catalogueDetail?.seriesId != null ? Number(catalogueDetail.seriesId) : undefined;
    const catalogueManufacturerId =
      catalogueDetail?.manufacturerId != null ? Number(catalogueDetail.manufacturerId) : undefined;

    const placementsByLuminaireUuid = new Map(
      (candidate.placements || []).map((placement) => [placement.tamanLuminaireUuid, placement]),
    );

    const modelLayers = (yield select((state: RootState) => state.workspace.modelLayers)) as ModelLayer[];
    const norms = ((yield select((state: RootState) => state.user?.norms)) ?? []) as { id: number; name: string }[];
    const workspaceLamps = (yield select((state: RootState) => state.workspace.lamps)) as Lamp[];
    const workspaceHeightByUuid = new Map(workspaceLamps.map((lamp) => [lamp.uuid, lamp.distanceToGround]));
    const payload = {
      name: `${sourceVersion.name} with ${productName}`,
      lights: sourceVersion.lights.map((light) => {
        const placement = placementsByLuminaireUuid.get(light.uuid);
        const liveHeight = workspaceHeightByUuid.get(light.uuid);
        const distanceToGround = liveHeight != null && liveHeight > 0 ? liveHeight : light.distanceToGround;
        return {
          name: light.name,
          uuid: light.uuid,
          positionX: light.positionX,
          positionY: light.positionY,
          positionZ: light.positionZ,
          rotationX: light.rotationX || 0,
          rotationY: light.rotationY || 0,
          rotationZ: light.rotationZ || 0,
          latitude: light.latitude,
          longitude: light.longitude,
          distanceToGround,
          // TAMAN-optimized lumenOutput/iesFileId are intentionally dropped: the
          // luminaire is now the matched catalogue product, not the custom fixture.
          productId: placement ? candidate.lampId : undefined,
          variantId: placement?.variantId,
          seriesId: placement ? catalogueSeriesId : undefined,
          manufacturerId: placement ? catalogueManufacturerId : undefined,
          rotationRadians: placement?.rotationRadians,
          marketMatchGroup: light.marketMatchGroup,
        };
      }),
      meshes: modelLayers.map((layer) => {
        const requirement = requirementForSave(layer, norms);
        return {
          uuid: layer.uuid,
          groupId: layer.groupId,
          name: layer.originalName,
          displayName: layer.displayName,
          color: layer.color,
          type: layer.layerType.toUpperCase(),
          normName: requirement.normName || null,
          ...(requirement.targets ? { targets: requirement.targets } : {}),
          canHoldLight: layer.canHoldLuminaire,
          isIlluminated: layer.isIlluminated,
        };
      }),
      meshGroups: (yield select((state: RootState) => state.workspace.meshGroups)) as MeshGroup[],
    };

    const { data: newVersion }: AxiosResponse<Version> = yield call(
      httpPOST,
      `/projects/${projectId}/versions`,
      payload,
    );

    const updatedProject = {
      ...project,
      versions: [...project.versions, newVersion],
    };
    yield put(setProject(updatedProject));

    history.push(`/workspace?projectId=${projectId}&versionId=${newVersion.id}`);

    yield put(
      showToast({
        message: 'workspace.toast.marketMatchVersionCreated',
        type: 'success',
      }),
    );
  } catch (error) {
    console.error('Error creating version from market match:', error);
    yield put(
      showToast({
        message: 'workspace.toast.createMarketMatchVersionFailed',
        type: 'error',
      }),
    );
  } finally {
    yield put(finishCreateVersion());
  }
}

function* handleDeleteVersion(action: ReturnType<typeof deleteVersion>): Generator<unknown, void, any> {
  try {
    const { projectId, versionId } = action.payload;

    yield call(httpDELETE, `/projects/${projectId}/versions/${versionId}`, {});

    const project: Project = yield select((state: RootState) => state.workspace.project);
    const updatedVersions = project.versions.filter((v) => v.id !== versionId);

    yield put(
      setProject({
        ...project,
        versions: updatedVersions,
      }),
    );

    yield put(
      showToast({
        message: 'workspace.toast.versionDeleted',
        type: 'success',
      }),
    );
  } catch (error) {
    console.error('Error deleting version:', error);
    yield put(
      showToast({
        message: 'workspace.toast.deleteVersionFailed',
        type: 'error',
      }),
    );
  }
}

function* handleRenameVersion(action: ReturnType<typeof renameVersion>): Generator<unknown, void, any> {
  try {
    const { projectId, versionId, newName } = action.payload;

    const project: Project = yield select((state: RootState) => state.workspace.project);
    const version = project.versions.find((v) => v.id === versionId);

    if (!version) {
      throw new Error('Version not found');
    }

    const { data: updatedVersion }: AxiosResponse<Version> = yield call(
      httpPUT,
      `/projects/${projectId}/versions/${versionId}`,
      {
        name: newName,
        optimisticLockKey: version.optimisticLockKey,
      },
    );

    const updatedVersions = project.versions.map((v) =>
      v.id === versionId ? { ...v, name: updatedVersion.name, optimisticLockKey: updatedVersion.optimisticLockKey } : v,
    );

    yield put(
      setProject({
        ...project,
        versions: updatedVersions,
      }),
    );
  } catch (error) {
    console.error('Error renaming version:', error);
    yield put(
      showToast({
        message: 'workspace.toast.renameVersionFailed',
        type: 'error',
      }),
    );
  }
}

export function* handleStartLampSimilarity(
  action: ReturnType<typeof startLampSimilarity>,
): Generator<unknown, void, any> {
  try {
    const { projectId, versionId, filters } = action.payload;

    const { data: updatedVersion }: AxiosResponse<Version> = yield call(
      httpPOST,
      `/projects/${projectId}/versions/${versionId}/lamp-comparison/start`,
      filters ?? {},
    );

    yield put(updateActiveVersion(updatedVersion));
  } catch (error) {
    console.error('Error starting lamp similarity:', error);
    yield put(
      showToast({
        message: 'workspace.toast.startLampSimilarityFailed',
        type: 'error',
      }),
    );
  } finally {
    yield put(finishLampSimilarity());
  }
}

interface CatalogueProductDetails {
  id: number;
  name?: string;
  imageUrl?: string;
  seriesId?: string;
  manufacturerId?: string;
  variants?: Array<{ id: string | number; name?: string }>;
}

async function fetchManufacturerNames(): Promise<Map<string, string>> {
  try {
    const { data }: AxiosResponse<Array<{ id: string; name: string }>> = await httpGET('/catalogue/manufacturers');
    return Array.isArray(data) ? new Map(data.map((m) => [String(m.id), m.name])) : new Map();
  } catch (error) {
    console.error('Error fetching manufacturers:', error);
    return new Map();
  }
}

/** Catalogue products by id; falls back to one request per product if the batch endpoint fails. */
async function fetchCatalogueProducts(ids: number[]): Promise<Map<number, CatalogueProductDetails>> {
  if (ids.length === 0) return new Map();
  try {
    const { data }: AxiosResponse<CatalogueProductDetails[]> = await httpGET(
      `/catalogue/products?ids=${ids.join(',')}`,
    );
    return new Map(data.map((product) => [Number(product.id), product]));
  } catch (batchError) {
    console.warn('Batch product request failed, fetching products one by one:', batchError);
  }
  const products = await Promise.all(
    ids.map((id) =>
      httpGET(`/catalogue/products/${id}`).then(
        ({ data }: AxiosResponse<CatalogueProductDetails>) => [id, data] as const,
        (error) => {
          console.error(`Error fetching product details for lamp ${id}:`, error);
          return null;
        },
      ),
    ),
  );
  return new Map(products.filter((entry) => entry != null));
}

/** Downloads measurement result files; files that fail to load are left out. */
async function fetchMeasurementFilesData(fileIds: number[]): Promise<Map<number, MeasurementData[]>> {
  if (fileIds.length === 0) return new Map();
  const presign = async (ids: number[]): Promise<BatchPresignedUrlResponse[]> =>
    (await httpPOST('/files/presigned-url/batch-download', { ids })).data;
  let urls: BatchPresignedUrlResponse[];
  try {
    urls = await presign(fileIds);
  } catch (batchError) {
    // One inaccessible file fails the whole batch; retry per file so the others still load.
    console.warn('Batch presign failed, retrying per measurement file:', batchError);
    const perFile = await Promise.all(fileIds.map((id) => presign([id]).catch(() => [])));
    urls = perFile.flat();
  }
  const files = await Promise.all(
    urls.map(async ({ id, url }) => {
      try {
        const response = await fetch(url);
        return response.ok ? ([id, (await response.json()) as MeasurementData[]] as const) : null;
      } catch (error) {
        console.error(`Error fetching measurement file ${id}:`, error);
        return null;
      }
    }),
  );
  return new Map(files.filter((entry) => entry != null));
}

function* handleLoadLampSimilarityDetails(): Generator<unknown, void, any> {
  try {
    const activeVersion: Version | undefined = yield select(getActiveVersion);
    const requestedVersionId = activeVersion?.id;
    if (!requestedVersionId) {
      return;
    }
    if (!activeVersion?.lampSimilarityResult?.candidates?.length) {
      return;
    }

    // Check if already loaded
    const existingDetails: LampSimilarityDetail[] = yield select(
      (state: RootState) => state.workspace.lampSimilarityDetails,
    );
    if (existingDetails.length > 0) {
      return;
    }

    yield put(setLoadingLampSimilarityDetails(true));

    const candidates: RankedCandidate[] = activeVersion.lampSimilarityResult.candidates;
    const productIds = [...new Set(candidates.map((c) => c.lampId))];
    const measurementFileIds = [
      ...new Set(candidates.map((c) => c.measurementFile?.id).filter((id): id is number => id != null)),
    ];

    // Manufacturers, products and measurement files load in parallel, a few requests in total.
    const [manufacturersMap, productsById, measurementDataByFileId]: [
      Map<string, string>,
      Map<number, CatalogueProductDetails>,
      Map<number, MeasurementData[]>,
    ] = yield all([
      call(fetchManufacturerNames),
      call(fetchCatalogueProducts, productIds),
      call(fetchMeasurementFilesData, measurementFileIds),
    ]);

    const details: LampSimilarityDetail[] = candidates.map((candidate) => {
      const productData = productsById.get(candidate.lampId);
      if (!productData) {
        // Still add the lamp with minimal info
        return { lampId: candidate.lampId, measurementFileId: candidate.measurementFile?.id };
      }
      const manufacturerId = productData.manufacturerId;
      const variants = (productData.variants ?? [])
        .filter((v) => v.name != null && v.name !== '')
        .map((v) => ({ id: v.id, name: v.name as string }));
      return {
        lampId: candidate.lampId,
        measurementFileId: candidate.measurementFile?.id,
        name: productData.name,
        imageUrl: productData.imageUrl,
        measurementData:
          candidate.measurementFile?.id != null
            ? (measurementDataByFileId.get(candidate.measurementFile.id) ?? null)
            : null,
        seriesId: productData.seriesId,
        manufacturerId,
        manufacturerName: manufacturerId != null ? manufacturersMap.get(String(manufacturerId)) : undefined,
        variants,
      };
    });

    const currentVersionId = (yield select((state: RootState) => state.workspace.project?.activeVersionId)) as
      | number
      | undefined;
    if (currentVersionId !== requestedVersionId) {
      return;
    }

    yield put(setLampSimilarityDetails(details));
  } catch (error) {
    console.error('Error loading lamp similarity details:', error);
    yield put(
      showToast({
        message: 'workspace.toast.loadLampSimilarityDetailsFailed',
        type: 'error',
      }),
    );
  } finally {
    yield put(setLoadingLampSimilarityDetails(false));
  }
}

function* handleApplyLampSimilarityMeasurement(
  action: PayloadAction<{ lampId: number; measurementFileId?: number; previewMode: 'ml' | 'frontend' }>,
): Generator<unknown, void, any> {
  const { lampId, measurementFileId } = action.payload;
  const state: RootState = yield select();
  const appliedSelection = state.workspace.appliedLampSimilaritySelection;
  revokeThirdPartyWireframeUrls(state.workspace.lampWireframes.thirdPartyWireframes);
  // Unapply: reducer already cleared wireframes; nothing else to do
  if (
    !appliedSelection ||
    appliedSelection.lampId !== lampId ||
    appliedSelection.measurementFileId !== measurementFileId
  )
    return;

  const activeVersion: Version | undefined = yield select(getActiveVersion);
  if (!activeVersion?.lampSimilarityResult?.candidates) return;

  const candidate: RankedCandidate | undefined = activeVersion.lampSimilarityResult.candidates.find(
    (c) => c.lampId === lampId && c.measurementFile?.id === measurementFileId,
  );
  if (!candidate?.placements?.length) {
    yield put(setThirdPartyWireframes(null));
    return;
  }

  const lightsByUuid = new Map<string, Light>();
  for (const light of activeVersion.lights || []) {
    lightsByUuid.set(light.uuid, light);
  }

  const loadingPlaceholders: LampWireframeThirdPartyEntry[] = candidate.placements
    .filter((p) => lightsByUuid.has(p.tamanLuminaireUuid))
    .map((p) => ({ lampUuid: p.tamanLuminaireUuid }));
  yield put(setThirdPartyWireframes(loadingPlaceholders));

  const wireframes: LampWireframeThirdPartyEntry[] = yield call(
    buildCatalogueWireframeEntries,
    candidate.placements.map((placement) => {
      const light = lightsByUuid.get(placement.tamanLuminaireUuid);
      return {
        lampUuid: placement.tamanLuminaireUuid,
        position: (light ? [light.positionX, light.positionY, light.positionZ] : [0, 0, 0]) as [number, number, number],
        productId: candidate.lampId,
        variantId: placement.variantId,
        rotationRadians: placement.rotationRadians,
      };
    }),
  );

  yield put(setThirdPartyWireframes(wireframes.length > 0 ? wireframes : null));

  if (appliedSelection.previewMode === 'frontend') {
    yield put(setFrontendMeasurementData(null));
    requestFrontendMeasurementCalculation();
  }
}

function* persistMarketMatchGroupChanges(): Generator<unknown, void, any> {
  const activeVersion: Version | undefined = yield select(getActiveVersion);
  if (!canPersistMarketMatchGroupChanges(activeVersion)) return;

  const project: Project = yield select((state: RootState) => state.workspace.project);
  if (!project?.activeVersionId || !activeVersion) return;

  yield put(
    updateProjectVersion({
      projectId: project.id,
      versionId: activeVersion.id,
      optimisticLockKey: activeVersion.optimisticLockKey,
    }),
  );
}

function* handleUpdateLampMarketMatchGroup(
  _action: ReturnType<typeof updateLampMarketMatchGroup>,
): Generator<unknown, void, any> {
  yield call(persistMarketMatchGroupChanges);
}

function* handleDeleteMarketMatchGroup(
  _action: ReturnType<typeof deleteMarketMatchGroup>,
): Generator<unknown, void, any> {
  yield call(persistMarketMatchGroupChanges);
}

export default function* workspaceSaga() {
  yield takeLatest(triggerClearWorkspaceState.type, handleClearWorkspaceState);
  yield takeLatest(loadProject.type, handleLoadProject);
  yield takeLatest(startTamanOptimization.type, handleTamanOptimization);
  yield takeLatest(startAutoPositioning.type, handleAutoPositioning);
  yield takeLatest(cancelAutoPositioning.type, handleCancelAutoPositioning);
  yield takeLatest(startLampSimilarity.type, handleStartLampSimilarity);
  yield takeLatest(loadLampSimilarityDetails.type, handleLoadLampSimilarityDetails);
  yield takeLatest(applyLampSimilarityMeasurement.type, handleApplyLampSimilarityMeasurement);
  yield takeLeading(updateProjectVersion.type, handleUpdateProjectVersion);
  yield takeLatest(loadMeasurementFile.type, handleLoadMeasurementFile);
  yield takeLatest(loadWireframeFiles.type, handleLoadWireframeFiles);
  yield takeLatest(updateLampProductVariant.type, handleRefreshCatalogueWireframes);
  yield takeLatest(updateLampRotationRadians.type, handleUpdateWireframeRotation);
  yield takeLatest(updateLampMarketMatchGroup.type, handleUpdateLampMarketMatchGroup);
  yield takeLatest(deleteMarketMatchGroup.type, handleDeleteMarketMatchGroup);
  yield takeLatest(loadBakedModelFile.type, handleLoadBakedModelFile);
  yield takeLatest(downloadReportImages.type, handleDownloadReportImages);
  yield takeLatest(downloadIesFile.type, handleDownloadIesFile);
  yield takeLatest(downloadBlenderProject.type, handleDownloadBlenderProject);
  yield takeLatest(startPeriodicChecking.type, handleStartPeriodicChecking);
  yield takeLatest(stopPeriodicChecking.type, handleStopPeriodicChecking);
  yield takeLatest(switchActiveVersion.type, handleSwitchActiveVersion);
  yield takeLatest(clearLampWireframes.type, handleClearLampWireframes);
  yield takeLatest(clearThirdPartyLampSimilarity.type, handleClearThirdPartyLampSimilarity);
  yield takeLatest(createEmptyVersion.type, handleCreateEmptyVersion);
  yield takeLatest(duplicateVersion.type, handleDuplicateVersion);
  yield takeLatest(createVersionFromMarketMatch.type, handleCreateVersionFromMarketMatch);
  yield takeLatest(deleteVersion.type, handleDeleteVersion);
  yield takeLatest(renameVersion.type, handleRenameVersion);
}
